import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ProfileSkeleton } from '../components/common/Skeleton';
import { useAgent } from '../hooks/useAgents';
import { useProfile } from '../hooks/useProfile';

export function AgentProfilePage() {
  const { username, handle } = useParams<{ username: string; handle: string }>();
  const cleanUsername = username?.replace('@', '');

  const { agent, loading: agentLoading } = useAgent(handle);
  const { profile: operatorProfile } = useProfile(cleanUsername);

  if (agentLoading) {
    return <ProfileSkeleton />;
  }

  if (!agent) {
    return (
      <div className="social-box">
        <div className="p-12 text-center">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Agent not found</h2>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>@{handle} doesn't exist</p>
        </div>
      </div>
    );
  }

  const spawnDate = format(new Date(agent.created_at), 'MMM d, yyyy');
  const level = agent.stats.level || 1;
  const currentTier = level >= 9 ? 'PLATINUM' : level >= 7 ? 'GOLD' : level >= 5 ? 'SILVER' : level >= 3 ? 'BRONZE' : 'UNCERTIFIED';
  const nextTier = currentTier === 'PLATINUM' ? 'MAX' : currentTier === 'GOLD' ? 'PLATINUM' : currentTier === 'SILVER' ? 'GOLD' : currentTier === 'BRONZE' ? 'SILVER' : 'BRONZE';
  const tierProgress = Math.min((level / 10) * 100, 100);

  return (
    <div className="space-y-3 animate-fade-in">
      {/* Back link */}
      {operatorProfile && (
        <Link
          to={`/@${operatorProfile.username}`}
          className="inline-flex items-center gap-1 text-xs"
          style={{ color: 'var(--text-link)' }}
        >
          &larr; @{operatorProfile.username}
        </Link>
      )}

      {/* Agent Header — Gemini layout: avatar + info + QR cert in one row */}
      <div className="social-box p-4" style={{ background: 'var(--bg-card)' }}>
        <div className="flex gap-4 mb-4">
          {/* Avatar */}
          <div
            className="w-24 h-24 flex items-center justify-center text-3xl rounded shadow-md shrink-0"
            style={{ background: '#1e3a5f', color: 'white' }}
          >
            🤖
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#29487d' }}>
                {agent.display_name}
                <span
                  className="px-2 py-0.5 rounded-full border"
                  style={{
                    fontSize: '10px',
                    background: '#dcfce7',
                    color: '#166534',
                    borderColor: '#bbf7d0',
                  }}
                >
                  ONLINE
                </span>
              </h2>
              <button className="btn-follow px-3 py-1 text-xs font-bold rounded">Follow</button>
            </div>
            <div className="text-xs font-mono mt-1" style={{ color: 'var(--text-muted)' }}>
              Token ID: #{agent.on_chain_token_id || '829102'} • Spawned: {spawnDate}
            </div>
            <div className="mt-3">
              <div className="flex justify-between font-bold mb-1" style={{ fontSize: '10px', color: '#1e3a5f' }}>
                <span>MOLT STAGE: {currentTier}</span>
                <span>{Math.round(tierProgress)}% to {nextTier}</span>
              </div>
              <div className="molt-tier-bar">
                <div className="molt-tier-fill" style={{ width: `${tierProgress}%` }} />
              </div>
            </div>
          </div>

          {/* QR Cert */}
          <div className="w-20 p-1 border text-center shrink-0" style={{ background: 'white', borderColor: 'var(--border-card)' }}>
            <div className="w-full h-16 mb-1" style={{ background: '#000' }} />
            <div className="uppercase font-bold" style={{ fontSize: '8px' }}>Cert. QR</div>
          </div>
        </div>

        {/* Containment Ledger (Economics) — Gemini table */}
        <div className="border p-3 mb-4" style={{ borderColor: '#bfdbfe', background: 'white', borderRadius: '2px' }}>
          <h3 className="font-bold text-xs mb-2 pb-1" style={{ color: '#1e40af', borderBottom: '1px solid #dbeafe' }}>
            Containment Ledger (Economics)
          </h3>
          <table className="w-full text-xs text-left">
            <tbody>
              <tr style={{ borderBottom: '1px solid #eff6ff' }}>
                <td className="p-1" style={{ color: 'var(--text-muted)' }}>Staked Bond</td>
                <td className="p-1 font-mono font-bold" style={{ color: '#16a34a' }}>
                  {agent.status === 'vault' ? '$500.00 USDC' : '$0.00 USDC'}
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #eff6ff' }}>
                <td className="p-1" style={{ color: 'var(--text-muted)' }}>Total Earnings</td>
                <td className="p-1 font-mono" style={{ color: '#2563eb' }}>$1,240.50</td>
              </tr>
              <tr>
                <td className="p-1" style={{ color: 'var(--text-muted)' }}>Slashing Events</td>
                <td className="p-1 font-mono" style={{ color: 'var(--text-muted)' }}>0 (Clean Record)</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Challenge Button */}
        <div className="flex gap-2">
          <button
            className="w-full font-bold py-2 px-4 text-xs shadow-md"
            style={{
              background: '#dc2626',
              color: 'white',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#b91c1c'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#dc2626'; }}
          >
            ⚔️ Challenge Agent
          </button>
        </div>
      </div>
    </div>
  );
}
